// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Platform } from 'react-native';

type NavigatorWithUserAgentData = Navigator & {
  userAgentData?: {
    brands?: Array<{ brand?: string; version?: string }>;
    platform?: string;
    platformVersion?: string;
  };
};

type BrowserInfo = {
  browserName?: string;
  browserVersion?: string;
};

type OperatingSystemInfo = {
  osName?: string;
  osVersion?: string;
};

const WINDOWS_VERSION_MAP: Record<string, string> = {
  '10.0': '10.0',
  '6.3': '8.1',
  '6.2': '8',
  '6.1': '7',
  '6.0': 'Vista',
  '5.1': 'XP',
};

const parseBrowserFromUserAgent = (userAgent: string): BrowserInfo => {
  const patterns: Array<{ name: string; pattern: RegExp }> = [
    { name: 'Microsoft Edge', pattern: /Edg(?:A|iOS)?\/([0-9.]+)/i },
    { name: 'Opera', pattern: /OPR\/([0-9.]+)/i },
    { name: 'Firefox', pattern: /(?:Firefox|FxiOS)\/([0-9.]+)/i },
    { name: 'Chrome', pattern: /(?:Chrome|CriOS)\/([0-9.]+)/i },
    { name: 'Safari', pattern: /Version\/([0-9.]+).*Safari/i },
  ];

  for (const candidate of patterns) {
    const match = userAgent.match(candidate.pattern);
    if (match) {
      return {
        browserName: candidate.name,
        browserVersion: match[1],
      };
    }
  }

  return {};
};

const parseOperatingSystemFromUserAgent = (userAgent: string): OperatingSystemInfo => {
  const windowsMatch = userAgent.match(/Windows NT ([0-9.]+)/i);
  if (windowsMatch) {
    return {
      osName: 'Windows',
      osVersion: WINDOWS_VERSION_MAP[windowsMatch[1]] ?? windowsMatch[1],
    };
  }

  const androidMatch = userAgent.match(/Android ([0-9.]+)/i);
  if (androidMatch) {
    return {
      osName: 'Android',
      osVersion: androidMatch[1],
    };
  }

  const iosMatch = userAgent.match(/(?:iPhone|CPU(?: iPhone)? OS) ([0-9_]+)/i);
  if (iosMatch) {
    return {
      osName: 'iOS',
      osVersion: iosMatch[1].replace(/_/g, '.'),
    };
  }

  const ipadMatch = userAgent.match(/iPad.*OS ([0-9_]+)/i);
  if (ipadMatch) {
    return {
      osName: 'iPadOS',
      osVersion: ipadMatch[1].replace(/_/g, '.'),
    };
  }

  const macOsMatch = userAgent.match(/Mac OS X ([0-9_]+)/i);
  if (macOsMatch) {
    return {
      osName: 'macOS',
      osVersion: macOsMatch[1].replace(/_/g, '.'),
    };
  }

  if (/Linux/i.test(userAgent)) {
    return {
      osName: 'Linux',
    };
  }

  return {};
};

const getBrowserInfo = (navigatorLike: NavigatorWithUserAgentData): BrowserInfo => {
  const brands = navigatorLike.userAgentData?.brands ?? [];
  const brand = brands.find((entry) => entry.brand && !/Not(?:\(|\s)A/i.test(entry.brand));
  if (brand?.brand) {
    return {
      browserName: brand.brand,
      browserVersion: brand.version,
    };
  }

  return parseBrowserFromUserAgent(navigatorLike.userAgent ?? '');
};

const getOperatingSystemInfo = (navigatorLike: NavigatorWithUserAgentData): OperatingSystemInfo => {
  if (navigatorLike.userAgentData?.platform) {
    return {
      osName: navigatorLike.userAgentData.platform,
      osVersion: navigatorLike.userAgentData.platformVersion,
    };
  }

  return parseOperatingSystemFromUserAgent(navigatorLike.userAgent ?? '');
};

export const getClientEnvironmentDetails = (): Record<string, unknown> => {
  const appVersion = typeof process !== 'undefined' ? process.env.EXPO_PUBLIC_APP_VERSION : undefined;
  const details: Record<string, unknown> = {
    runtimePlatform: Platform.OS,
  };

  if (appVersion) {
    details.appVersion = appVersion;
  }

  if (Platform.OS !== 'web') {
    if (Platform.Version != null) {
      details.osName = Platform.OS;
      details.osVersion = String(Platform.Version);
    }
    return details;
  }

  if (typeof navigator === 'undefined') {
    return details;
  }

  const navigatorLike = navigator as NavigatorWithUserAgentData;
  const browserInfo = getBrowserInfo(navigatorLike);
  const operatingSystemInfo = getOperatingSystemInfo(navigatorLike);

  details.userAgent = navigatorLike.userAgent ?? '';
  details.language = navigatorLike.language ?? '';

  if (browserInfo.browserName) {
    details.browserName = browserInfo.browserName;
  }
  if (browserInfo.browserVersion) {
    details.browserVersion = browserInfo.browserVersion;
  }
  if (operatingSystemInfo.osName) {
    details.osName = operatingSystemInfo.osName;
  }
  if (operatingSystemInfo.osVersion) {
    details.osVersion = operatingSystemInfo.osVersion;
  }

  return details;
};
